import React, { useCallback, useMemo } from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import DataTable, { createColumnHelper } from "veneer/DataTable";
import {
  ContractIconButton,
  DeleteIconButton,
  ExpandIconButton,
} from "veneer/IconButton";
import { Colors } from "veneer/Theme";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

import {
  getArchiveStatus,
  isVideo,
} from "cortex/ui/src/fleet/view/media-library/utils";
import { Spot } from "cortex/ui/src/fleet/view/strategy/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { formatSpotPreview } from "cortex/ui/src/util/tableFormat";
import { durationToHuman } from "cortex/ui/src/util/time";

interface SystemStrategySpotTableProps {
  handleRowsChange: (rows: ReadonlyArray<Spot>) => void;
  readOnly?: boolean;
  rows: ReadonlyArray<Spot>;
}

const column = createColumnHelper<Spot>();

export default function SystemStrategySpotTable({
  handleRowsChange,
  readOnly = false,
  rows = [],
}: SystemStrategySpotTableProps) {
  const dispatch = useAppDispatch();
  const rowCount = rows.length;

  const handleChangePriorityOrder = useCallback(
    (dir: -1 | 1, row: Spot) => {
      const newRows = [...rows];
      const targetIdx = row._rowNum + dir;

      if (targetIdx < 0 || targetIdx >= newRows.length) return;

      const temp = newRows[targetIdx];
      newRows[targetIdx] = newRows[row._rowNum];
      newRows[row._rowNum] = temp;

      handleRowsChange(newRows);
      return;
    },
    [dispatch, handleRowsChange, rows],
  );

  const handleDeleteSpot = useCallback(
    (row: Spot) => {
      handleRowsChange(rows.filter((_, idx) => idx !== row._rowNum));
      return;
    },
    [dispatch, handleRowsChange, rows],
  );

  const columns = useMemo(
    () => [
      column.accessor("_rowNum", {
        header: "Order",
        cell: (prop) => prop.getValue() + 1,
        enableSorting: false,
        meta: {
          align: "center",
          width: 60,
        },
      }),
      column.accessor("url", {
        header: "Preview",
        cell: (prop) => formatSpotPreview(null, prop.row.original),
        enableSorting: false,
        meta: {
          align: "center",
          width: 125,
        },
      }),
      column.accessor("name", {
        header: "Name",
        cell: (prop) => {
          const row = prop.row.original;
          if (row.type === "application" && !row.available) {
            return "";
          }
          return <NameCell row={row} />;
        },
        enableSorting: false,
      }),
      column.accessor("duration", {
        header: "Duration",
        cell: (prop) => {
          const duration = prop.getValue();
          return isVideo(prop.row.original.mimeType)
            ? "N/A"
            : durationToHuman(duration);
        },
        enableSorting: false,
        meta: {
          width: 150,
        },
      }),
      column.accessor("friendlyType", {
        header: "Type",
        cell: (prop) => prop.getValue(),
        enableSorting: false,
        meta: {
          width: 150,
        },
      }),
      column.display({
        id: "actions",
        header: "Actions",
        cell: (prop) => {
          const row = prop.row.original;
          return (
            <Box>
              <ExpandIconButton
                disabled={row._rowNum === 0}
                onClick={() => {
                  handleChangePriorityOrder(-1, row);
                }}
              />
              <ContractIconButton
                disabled={row._rowNum === rowCount - 1}
                onClick={() => {
                  handleChangePriorityOrder(1, row);
                }}
              />
              <DeleteIconButton
                onClick={() => {
                  handleDeleteSpot(row);
                }}
              />
            </Box>
          );
        },
        meta: {
          width: 150,
        },
      }),
    ],
    [rowCount, handleChangePriorityOrder, handleDeleteSpot],
  );

  return (
    <Box mx={-2}>
      <DataTable
        columns={columns}
        emptyState={
          <Box p={2} textAlign="center">
            <Typography color={Colors.gray1000} variant="body2">
              There is no data to display
            </Typography>
          </Box>
        }
        getRowProps={(row) => ({
          inactive: Boolean(
            getArchiveStatus(row as Parameters<typeof getArchiveStatus>[0]),
          ),
        })}
        hideColumns={readOnly ? ["actions"] : []}
        rows={rows}
      />
    </Box>
  );
}

function NameCell({ row }: { row: Spot }) {
  const { archived, friendlyType, name } = row;
  return (
    <>
      <OverflowTooltipTypography variant="body2">
        {name}
      </OverflowTooltipTypography>
      {archived && (
        <Alert
          dense
          severity="warning"
          title={`This ${friendlyType} is archived.`}
        >
          It will still play, but you cannot add it to other content sources.
        </Alert>
      )}
    </>
  );
}
